import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  Trash2,
  Phone,
  User,
  ChevronRight,
  Check,
  MessageCircle,
  DollarSign,
  Percent,
  GripVertical,
  ExternalLink,
  Loader2,
  Copy,
  X,
  Pencil
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComensalesSplitDialog({ 
  open, 
  onOpenChange, 
  session, 
  onUpdate 
}) {
  const [comensales, setComensales] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('comensales'); // 'comensales', 'asignar', 'enviar'
  
  // Form para nuevo comensal
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoTelefono, setNuevoTelefono] = useState('');
  const [agregando, setAgregando] = useState(false);
  
  // Asignaciones de items
  const [asignaciones, setAsignaciones] = useState({});
  const [itemSeleccionado, setItemSeleccionado] = useState(null);
  const [showDividirModal, setShowDividirModal] = useState(false);
  const [porcentajesDividir, setPorcentajesDividir] = useState({});
  
  // Links de WhatsApp
  const [waLinks, setWaLinks] = useState([]);
  const [generandoLinks, setGenerandoLinks] = useState(false);
  
  // Drag & Drop
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverComensal, setDragOverComensal] = useState(null);
  
  // Editar comensal
  const [editingComensal, setEditingComensal] = useState(null);
  const [editNombre, setEditNombre] = useState('');
  const [editTelefono, setEditTelefono] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (open && session) {
      fetchComensales();
      fetchItems();
    }
  }, [open, session]);

  const fetchComensales = async () => {
    if (!session?.session_id) return;
    try {
      const response = await axios.get(`${API}/sessions/${session.session_id}/comensales`);
      setComensales(response.data.comensales || []);
    } catch (error) {
      console.error('Error fetching comensales:', error);
    }
  };

  const fetchItems = async () => {
    if (!session?.session_id) return;
    try {
      const response = await axios.get(`${API}/orders?session_id=${session.session_id}`);
      const orders = response.data.orders || response.data || [];
      const allItems = [];
      orders.forEach(order => {
        (order.items || []).forEach(item => {
          if (item.item_id) {
            allItems.push({
              ...item,
              order_id: order.order_id,
              subtotal: item.subtotal || (item.price * item.quantity)
            });
          }
        });
      });
      setItems(allItems);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const agregarComensal = async () => {
    if (!nuevoNombre.trim()) {
      toast.error('Ingresa un nombre');
      return;
    }
    
    setAgregando(true);
    try {
      const response = await axios.post(`${API}/sessions/${session.session_id}/comensales`, {
        nombre: nuevoNombre.trim(),
        telefono: nuevoTelefono.trim() || null
      });
      setComensales([...comensales, response.data]);
      setNuevoNombre('');
      setNuevoTelefono('');
      toast.success(`${response.data.nombre} agregado`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar');
    } finally {
      setAgregando(false);
    }
  };

  const eliminarComensal = async (comensalId) => {
    try {
      await axios.delete(`${API}/sessions/${session.session_id}/comensales/${comensalId}`);
      setComensales(comensales.filter(c => c.comensal_id !== comensalId));
      toast.success('Comensal eliminado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Abrir modal de edición
  const openEditComensal = (comensal) => {
    setEditingComensal(comensal);
    setEditNombre(comensal.nombre || '');
    setEditTelefono(comensal.telefono || '');
  };

  // Guardar edición
  const saveEditComensal = async () => {
    if (!editNombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setSavingEdit(true);
    try {
      const response = await axios.put(
        `${API}/sessions/${session.session_id}/comensales/${editingComensal.comensal_id}`,
        {
          nombre: editNombre.trim(),
          telefono: editTelefono.trim() || null
        }
      );
      
      // Actualizar en la lista local
      setComensales(comensales.map(c => 
        c.comensal_id === editingComensal.comensal_id ? response.data : c
      ));
      
      setEditingComensal(null);
      toast.success('Comensal actualizado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingEdit(false);
    }
  };

  // Asignar item completo a un comensal
  const asignarItemCompleto = (itemId, comensalId) => {
    setAsignaciones(prev => ({
      ...prev,
      [itemId]: [{ comensal_id: comensalId, porcentaje: 100 }]
    }));
  };

  // Abrir modal para dividir item
  const abrirDividirItem = (item) => {
    setItemSeleccionado(item);
    // Inicializar porcentajes iguales
    const pctInicial = Math.floor(100 / comensales.length);
    const pcts = {};
    comensales.forEach((c, i) => {
      pcts[c.comensal_id] = i === 0 ? 100 - (pctInicial * (comensales.length - 1)) : pctInicial;
    });
    setPorcentajesDividir(pcts);
    setShowDividirModal(true);
  };

  // Confirmar división de item
  const confirmarDivision = () => {
    const totalPct = Object.values(porcentajesDividir).reduce((a, b) => a + (parseInt(b) || 0), 0);
    if (totalPct !== 100) {
      toast.error(`Los porcentajes deben sumar 100% (actual: ${totalPct}%)`);
      return;
    }
    
    const asigs = Object.entries(porcentajesDividir)
      .filter(([, pct]) => parseInt(pct) > 0)
      .map(([comensal_id, porcentaje]) => ({
        comensal_id,
        porcentaje: parseInt(porcentaje)
      }));
    
    setAsignaciones(prev => ({
      ...prev,
      [itemSeleccionado.item_id]: asigs
    }));
    
    setShowDividirModal(false);
    setItemSeleccionado(null);
  };

  // Guardar asignaciones en backend
  const guardarAsignaciones = async () => {
    const asignacionesArray = [];
    Object.entries(asignaciones).forEach(([item_id, asigs]) => {
      asigs.forEach(asig => {
        asignacionesArray.push({
          item_id,
          comensal_id: asig.comensal_id,
          porcentaje: asig.porcentaje
        });
      });
    });
    
    if (asignacionesArray.length === 0) {
      toast.error('No hay items asignados');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/sessions/${session.session_id}/asignar-items`, {
        asignaciones: asignacionesArray
      });
      setComensales(response.data.comensales);
      toast.success('Items asignados correctamente');
      setStep('enviar');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar items');
    } finally {
      setLoading(false);
    }
  };

  // Generar links de WhatsApp
  const generarLinksWhatsApp = async () => {
    setGenerandoLinks(true);
    try {
      const response = await axios.post(`${API}/sessions/${session.session_id}/enviar-cuentas`);
      setWaLinks(response.data.resultados || []);
    } catch (error) {
      toast.error('Error al generar links');
    } finally {
      setGenerandoLinks(false);
    }
  };

  // Calcular totales
  const totalAsignado = comensales.reduce((sum, c) => sum + (c.subtotal || 0), 0);
  const totalItems = items.reduce((sum, i) => sum + (i.subtotal || 0), 0);
  const sinAsignar = totalItems - totalAsignado;

  // Obtener asignación actual de un item
  const getAsignacionItem = (itemId) => {
    return asignaciones[itemId] || [];
  };

  const getComensalNombre = (comensalId) => {
    const c = comensales.find(x => x.comensal_id === comensalId);
    return c ? c.nombre : '?';
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-500" />
              Dividir Cuenta por Comensales
            </DialogTitle>
            <DialogDescription>
              {session?.unit_name} - Total: ${session?.total?.toLocaleString()}
            </DialogDescription>
          </DialogHeader>

          {/* Steps indicator */}
          <div className="flex items-center justify-center gap-2 py-4">
            {['comensales', 'asignar', 'enviar'].map((s, i) => (
              <React.Fragment key={s}>
                <button
                  onClick={() => setStep(s)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
                    step === s 
                      ? 'bg-violet-500 text-white' 
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">
                    {i + 1}
                  </span>
                  {s === 'comensales' && 'Comensales'}
                  {s === 'asignar' && 'Asignar Items'}
                  {s === 'enviar' && 'Enviar'}
                </button>
                {i < 2 && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Comensales */}
          {step === 'comensales' && (
            <div className="space-y-4">
              {/* Agregar comensal */}
              <div className="p-4 border rounded-lg bg-muted/30">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Agregar Comensal
                </h4>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      placeholder="Nombre"
                      value={nuevoNombre}
                      onChange={(e) => setNuevoNombre(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && agregarComensal()}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder="Teléfono (opcional)"
                      value={nuevoTelefono}
                      onChange={(e) => setNuevoTelefono(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && agregarComensal()}
                    />
                  </div>
                  <Button onClick={agregarComensal} disabled={agregando}>
                    {agregando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Lista de comensales */}
              <div className="space-y-2">
                {comensales.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No hay comensales agregados</p>
                    <p className="text-sm">Agrega al menos 2 personas para dividir la cuenta</p>
                  </div>
                ) : (
                  comensales.map((c) => (
                    <div 
                      key={c.comensal_id}
                      className="flex items-center justify-between p-3 border rounded-lg bg-card"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                          <User className="w-5 h-5 text-violet-600" />
                        </div>
                        <div>
                          <p className="font-medium">{c.nombre}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            {c.telefono && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {c.telefono}
                              </span>
                            )}
                            <Badge variant="outline" className="text-xs">
                              {c.codigo}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {c.subtotal > 0 && (
                          <span className="font-medium text-green-600">
                            ${c.subtotal.toLocaleString()}
                          </span>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openEditComensal(c)}
                          disabled={c.pagado}
                          title="Editar comensal"
                        >
                          <Pencil className="w-4 h-4 text-blue-500" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => eliminarComensal(c.comensal_id)}
                          disabled={c.pagado}
                          title="Eliminar comensal"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={() => setStep('asignar')}
                  disabled={comensales.length < 2}
                >
                  Siguiente: Asignar Items
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 2: Asignar Items */}
          {step === 'asignar' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                <GripVertical className="w-4 h-4 inline mr-1" />
                Arrastra los items hacia un comensal o usa los botones
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Items del pedido (Draggable) */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3">Items del Pedido</h4>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {items.map((item) => {
                      const asig = getAsignacionItem(item.item_id);
                      const asignado = asig.length > 0;
                      
                      return (
                        <div 
                          key={item.item_id}
                          draggable={!asignado}
                          onDragStart={(e) => {
                            setDraggedItem(item);
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragEnd={() => setDraggedItem(null)}
                          className={`p-3 border rounded-lg transition-all ${
                            asignado 
                              ? 'bg-green-50 dark:bg-green-900/20 border-green-300' 
                              : 'bg-card cursor-grab active:cursor-grabbing hover:shadow-md hover:border-violet-300'
                          } ${draggedItem?.item_id === item.item_id ? 'opacity-50 scale-95' : ''}`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              {!asignado && (
                                <GripVertical className="w-4 h-4 text-muted-foreground" />
                              )}
                              <div>
                                <p className="font-medium">{item.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {item.quantity} x ${item.price?.toLocaleString()}
                                </p>
                              </div>
                            </div>
                            <span className="font-medium">${item.subtotal?.toLocaleString()}</span>
                          </div>
                          
                          {asignado ? (
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-green-700 dark:text-green-400">
                                {asig.map(a => (
                                  <span key={a.comensal_id} className="mr-2">
                                    {getComensalNombre(a.comensal_id)} {a.porcentaje < 100 && `(${a.porcentaje}%)`}
                                  </span>
                                ))}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newAsig = {...asignaciones};
                                  delete newAsig[item.item_id];
                                  setAsignaciones(newAsig);
                                }}
                                className="h-6 px-2 text-xs text-red-500 hover:text-red-700"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex gap-1 flex-wrap">
                              {comensales.map(c => (
                                <Button
                                  key={c.comensal_id}
                                  variant="outline"
                                  size="sm"
                                  className="text-xs h-7"
                                  onClick={() => asignarItemCompleto(item.item_id, c.comensal_id)}
                                >
                                  {c.nombre}
                                </Button>
                              ))}
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs h-7 border-dashed"
                                onClick={() => abrirDividirItem(item)}
                              >
                                <Percent className="w-3 h-3 mr-1" />
                                Dividir
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Resumen por comensal (Drop targets) */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3">Arrastra items aquí</h4>
                  <div className="space-y-3">
                    {comensales.map(c => {
                      // Calcular subtotal de asignaciones actuales
                      let subtotal = 0;
                      const itemsAsignados = [];
                      Object.entries(asignaciones).forEach(([itemId, asigs]) => {
                        const item = items.find(i => i.item_id === itemId);
                        if (!item) return;
                        const asig = asigs.find(a => a.comensal_id === c.comensal_id);
                        if (asig) {
                          subtotal += (item.subtotal * asig.porcentaje / 100);
                          itemsAsignados.push({
                            ...item,
                            porcentaje: asig.porcentaje,
                            monto: (item.subtotal * asig.porcentaje / 100)
                          });
                        }
                      });
                      
                      const isOver = dragOverComensal === c.comensal_id;
                      
                      return (
                        <div 
                          key={c.comensal_id}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setDragOverComensal(c.comensal_id);
                          }}
                          onDragLeave={() => setDragOverComensal(null)}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (draggedItem) {
                              asignarItemCompleto(draggedItem.item_id, c.comensal_id);
                            }
                            setDragOverComensal(null);
                            setDraggedItem(null);
                          }}
                          className={`p-4 border-2 rounded-lg transition-all ${
                            isOver 
                              ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30 scale-[1.02]' 
                              : 'border-dashed border-muted-foreground/30 hover:border-muted-foreground/50'
                          }`}
                        >
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                                <User className="w-4 h-4 text-violet-600" />
                              </div>
                              <span className="font-medium">{c.nombre}</span>
                            </div>
                            <span className="text-xl font-bold text-violet-600">
                              ${Math.round(subtotal).toLocaleString()}
                            </span>
                          </div>
                          
                          {itemsAsignados.length > 0 ? (
                            <div className="space-y-1 mt-2 pt-2 border-t">
                              {itemsAsignados.map((item, idx) => (
                                <div key={idx} className="flex justify-between text-sm text-muted-foreground">
                                  <span>
                                    {item.name}
                                    {item.porcentaje < 100 && ` (${item.porcentaje}%)`}
                                  </span>
                                  <span>${Math.round(item.monto).toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground text-center mt-2">
                              {isOver ? '¡Suelta aquí!' : 'Arrastra items aquí'}
                            </p>
                          )}
                        </div>
                      );
                    })}
                    
                    <div className="border-t pt-3 mt-3">
                      <div className="flex justify-between text-sm">
                        <span>Total Items:</span>
                        <span>${totalItems.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>Sin asignar:</span>
                        <span>${(totalItems - Object.entries(asignaciones).reduce((sum, [itemId, asigs]) => {
                          const item = items.find(i => i.item_id === itemId);
                          if (!item) return sum;
                          const totalPct = asigs.reduce((s, a) => s + a.porcentaje, 0);
                          return sum + (item.subtotal * totalPct / 100);
                        }, 0)).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setStep('comensales')}>
                  Volver
                </Button>
                <Button onClick={guardarAsignaciones} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Confirmar y Continuar
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 3: Enviar */}
          {step === 'enviar' && (
            <div className="space-y-4">
              <div className="text-center py-4">
                <Check className="w-12 h-12 mx-auto text-green-500 mb-2" />
                <h3 className="text-lg font-medium">Items asignados correctamente</h3>
                <p className="text-muted-foreground">Ahora puedes enviar la cuenta a cada comensal</p>
              </div>

              {/* Resumen final */}
              <div className="space-y-2">
                {comensales.map(c => (
                  <div 
                    key={c.comensal_id}
                    className="p-4 border rounded-lg flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                        <User className="w-5 h-5 text-violet-600" />
                      </div>
                      <div>
                        <p className="font-medium">{c.nombre}</p>
                        <p className="text-sm text-muted-foreground">
                          {c.items?.length || 0} items - {c.telefono || 'Sin teléfono'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-violet-600">
                        ${c.subtotal?.toLocaleString()}
                      </p>
                      <Badge variant={c.pagado ? "default" : "outline"}>
                        {c.pagado ? 'Pagado' : 'Pendiente'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>

              {/* Botón generar links */}
              <Button 
                onClick={generarLinksWhatsApp} 
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={generandoLinks}
              >
                {generandoLinks ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <MessageCircle className="w-4 h-4 mr-2" />
                )}
                Generar Links de WhatsApp
              </Button>

              {/* Links generados */}
              {waLinks.length > 0 && (
                <div className="space-y-2 border-t pt-4">
                  <h4 className="font-medium">Links para enviar:</h4>
                  {waLinks.map(link => (
                    <div key={link.comensal_id} className="p-3 border rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">{link.nombre}</span>
                        <span className="text-violet-600">${link.subtotal?.toLocaleString()}</span>
                      </div>
                      {link.wa_link ? (
                        <div className="flex gap-2">
                          <a
                            href={link.wa_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 flex items-center justify-center gap-2 p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                          >
                            <MessageCircle className="w-4 h-4" />
                            Enviar WhatsApp
                          </a>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(link.mensaje);
                              toast.success('Mensaje copiado');
                            }}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">Sin teléfono registrado</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cerrar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal para dividir item */}
      <Dialog open={showDividirModal} onOpenChange={setShowDividirModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dividir Item</DialogTitle>
            <DialogDescription>
              {itemSeleccionado?.name} - ${itemSeleccionado?.subtotal?.toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {comensales.map(c => (
              <div key={c.comensal_id} className="flex items-center gap-3">
                <span className="flex-1">{c.nombre}</span>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={porcentajesDividir[c.comensal_id] || 0}
                    onChange={(e) => setPorcentajesDividir(prev => ({
                      ...prev,
                      [c.comensal_id]: e.target.value
                    }))}
                    className="w-20 text-center"
                  />
                  <span className="text-muted-foreground">%</span>
                </div>
                <span className="w-20 text-right font-medium">
                  ${Math.round((itemSeleccionado?.subtotal || 0) * (parseInt(porcentajesDividir[c.comensal_id]) || 0) / 100).toLocaleString()}
                </span>
              </div>
            ))}
            
            <div className="border-t pt-3">
              <div className="flex justify-between font-medium">
                <span>Total:</span>
                <span className={
                  Object.values(porcentajesDividir).reduce((a, b) => a + (parseInt(b) || 0), 0) === 100
                    ? 'text-green-600'
                    : 'text-red-600'
                }>
                  {Object.values(porcentajesDividir).reduce((a, b) => a + (parseInt(b) || 0), 0)}%
                </span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDividirModal(false)}>
              Cancelar
            </Button>
            <Button onClick={confirmarDivision}>
              Confirmar División
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal para editar comensal */}
      <Dialog open={!!editingComensal} onOpenChange={(open) => !open && setEditingComensal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-500" />
              Editar Comensal
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                placeholder="Nombre del comensal"
                value={editNombre}
                onChange={(e) => setEditNombre(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input
                placeholder="Teléfono (opcional)"
                value={editTelefono}
                onChange={(e) => setEditTelefono(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Para recibir la cuenta por WhatsApp
              </p>
            </div>
            {editingComensal && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Código: <span className="font-mono font-medium">{editingComensal.codigo}</span>
                </p>
                {editingComensal.registrado_por === 'self' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Este comensal se registró por su cuenta
                  </p>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingComensal(null)}>
              Cancelar
            </Button>
            <Button onClick={saveEditComensal} disabled={savingEdit}>
              {savingEdit ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
